import { useEffect, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, FileText } from "lucide-react";
import { SiteLayout } from "./SiteLayout";
import { SiteBreadcrumbs } from "./SiteBreadcrumbs";

export type LegalSection = {
  id: string;
  title: string;
  body: ReactNode;
};

export type LegalPageProps = {
  eyebrow: string;
  title: string;
  italic: string;
  intro: string;
  updated: string;
  sections: LegalSection[];
  related?: { label: string; to: string }[];
};

export function LegalPage({ eyebrow, title, italic, intro, updated, sections, related }: LegalPageProps) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? "");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const els = sections
      .map((s) => document.getElementById(s.id))
      .filter((el): el is HTMLElement => !!el);
    if (els.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: "-96px 0px -60% 0px", threshold: [0, 1] }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [sections]);

  return (
    <SiteLayout>
      {/* HERO */}
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 pb-10 pt-20 md:px-6">
          <SiteBreadcrumbs className="mb-6" currentName={eyebrow} />
          <h1 className="font-display text-4xl leading-[1.05] md:text-6xl">
            {title} <em className="italic">{italic}</em>
          </h1>
          <p className="mt-6 max-w-2xl text-base text-muted-foreground md:text-lg">{intro}</p>
          <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            <FileText className="h-3.5 w-3.5" />
            Last updated · {updated}
          </p>
        </div>
      </section>

      {/* CONTENT */}
      <section className="bg-muted">
        <div className="mx-auto grid max-w-5xl gap-10 px-4 py-16 md:grid-cols-[220px_1fr] md:px-6">
          {/* TOC */}
          <aside className="md:sticky md:top-24 md:self-start">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">On this page</p>
            <nav className="mt-4 grid gap-1.5 border-l border-foreground/10 pl-4">
              {sections.map((s, i) => {
                const isActive = activeId === s.id;
                return (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className={`text-sm font-medium transition ${
                      isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <span className={`mr-2 ${isActive ? "text-foreground" : "text-muted-foreground/50"}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {s.title}
                  </a>
                );
              })}
            </nav>
          </aside>

          {/* BODY */}
          <div className="rounded-3xl bg-card p-6 md:p-12">
            <div className="space-y-12">
              {sections.map((s, i) => (
                <section key={s.id} id={s.id} className="scroll-mt-24">
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Section {String(i + 1).padStart(2, "0")}
                  </p>
                  <h2 className="mt-2 font-display text-2xl leading-tight md:text-3xl">{s.title}</h2>
                  <div className="prose-legal mt-5 space-y-4 text-[15px] leading-relaxed text-foreground/85">
                    {s.body}
                  </div>
                </section>
              ))}
            </div>

            {/* CONTACT FOOTER */}
            <div className="mt-16 rounded-2xl bg-muted p-6 md:p-8">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Need to talk?</p>
              <h3 className="mt-2 font-display text-xl md:text-2xl">
                Questions about this policy? <em className="italic">We're here.</em>
              </h3>
              <p className="mt-3 text-sm text-muted-foreground">
                Email us at{" "}
                <a href="mailto:hello@banana.cards" className="font-bold text-foreground underline-offset-4 hover:underline">
                  hello@banana.cards
                </a>{" "}
                — we read every message.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED */}
      {related && related.length > 0 && (
        <section className="bg-background">
          <div className="mx-auto max-w-5xl px-4 py-16 md:px-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Related policies</p>
            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              {related.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  className="group flex items-center justify-between gap-4 rounded-2xl bg-muted p-5 transition hover:bg-foreground hover:text-background"
                >
                  <span className="text-sm font-bold">{r.label}</span>
                  <ArrowUpRight className="h-4 w-4 transition group-hover:rotate-12" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </SiteLayout>
  );
}

/** Helper components for clean legal markup */
export function P({ children }: { children: ReactNode }) {
  return <p>{children}</p>;
}

export function UL({ items }: { items: ReactNode[] }) {
  return (
    <ul className="space-y-2 pl-5">
      {items.map((item, i) => (
        <li key={i} className="relative before:absolute before:-left-4 before:top-2.5 before:h-1 before:w-1 before:rounded-full before:bg-foreground">
          {item}
        </li>
      ))}
    </ul>
  );
}

export function H3({ children }: { children: ReactNode }) {
  return <h3 className="mt-6 font-display text-lg font-bold">{children}</h3>;
}